import React, { useId } from 'react';
import PropTypes from 'prop-types';
import './Slider.css';

/**
 * Slider — Design System
 *
 * Labelled range control: label on the left, formatted value on the right,
 * filled track below. Wraps a native <input type="range"> so keyboard
 * interaction, stepping and form semantics come for free.
 *
 * NOTE: not registered in scripts/sync-components.mjs → COMPONENTS_CONFIG.
 * Every entry there is keyed by a Figma nodeId and this control has no Figma
 * node yet. Add one (and the token sync) once the component is designed.
 */
const Slider = ({
  label = '',
  value,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  formatValue,
  showValue = true,
  disabled = false,
  id,
  className = '',
  ...rest
}) => {
  const generatedId = useId();
  const inputId = id || `ds-slider-${generatedId}`;

  const range = max - min;
  const clamped = Math.min(max, Math.max(min, value));
  const fill = range > 0 ? ((clamped - min) / range) * 100 : 0;
  const displayValue = typeof formatValue === 'function' ? formatValue(clamped) : clamped;

  const rootClass = ['ds-slider', disabled && 'ds-slider--disabled', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} style={{ '--ds-slider-fill': `${fill}%` }}>
      {(label || showValue) && (
        <div className="ds-slider__header">
          {label && (
            <label className="ds-slider__label typo-h5" htmlFor={inputId}>
              {label}
            </label>
          )}
          {showValue && <span className="ds-slider__value typo-body-sm">{displayValue}</span>}
        </div>
      )}

      <input
        id={inputId}
        type="range"
        className="ds-slider__input"
        min={min}
        max={max}
        step={step}
        value={clamped}
        disabled={disabled}
        onChange={(e) => onChange && onChange(Number(e.target.value))}
        {...rest}
      />
    </div>
  );
};

Slider.propTypes = {
  /** Text shown above the track, left-aligned */
  label: PropTypes.string,
  /** Controlled value */
  value: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  /** Receives the new value as a number */
  onChange: PropTypes.func,
  /** Formats the value shown above the track, e.g. (v) => `${v}%` */
  formatValue: PropTypes.func,
  showValue: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
};

export default Slider;
